// Affiliate Links page - displays affiliate link offer details with tabs

import { BoostRewardsSection } from "@/components/boosts";
import { ProjectPageHeader } from "@/components/projects/project-page-header";
import {
  AffiliateLinkCard,
  CreateDeepLinkDialog,
  OfferStatsDisplay,
  PaymentsTab,
  PayoutTierDetails,
  PromoCodeCard,
  ShareOfferDialog,
} from "@/components/promo-codes";
import {
  useProjectInteractionsQuery,
  type CreatorInteraction,
} from "@/lib/queries/projects";
import {
  isLinkOffer,
  isPromoOffer,
  useAffiliateOfferQuery,
  type AffiliateOffer,
} from "@/lib/queries/promo-codes";
import {
  Card,
  CardContent,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@repo/ui";
import { createFileRoute, useLocation } from "@tanstack/react-router";
import { Building2 } from "lucide-react";
import type { ReactNode } from "react";
import { useMemo } from "react";
import type { CampaignRouteContext } from "@/lib/types/route-context";
import { useDateFilter } from "@/lib/hooks/use-date-filter";
import type { DateRange, DateRangeOption } from "@/lib/types/date-filter";
import { dateRangeToApiParams } from "@/lib/utils/date-range";

export const Route = createFileRoute(
  "/(org)/o/$orgSlug/p/$profileSlug/c/$campaignId/affiliate-links/$interactionId",
)({
  component: AffiliateLinksPage,
});

// ============================================================================
// Presentational View (exported for Storybook)
// ============================================================================

export interface AffiliateLinksPageViewProps {
  isLoading: boolean;
  error: Error | null;
  interactionNotFound: boolean;
  interactionTitle: string;
  offerPending: boolean;
  offerError: Error | null;
  offer: AffiliateOffer | undefined;
  organizationId: string;
  orgSlug: string;
  profileSlug: string;
  campaignId: string;
  interactionId: string;
  secureUrl: string;
  locationPathname: string;
  isFetching: boolean;
  dateFilter: {
    selectedOption: DateRangeOption;
    dateRange: DateRange;
    onChange: (option: DateRangeOption, customRange?: DateRange) => void;
  };
  offerCreatedDate?: string;
}

export function AffiliateLinksPageView({
  isLoading,
  error,
  interactionNotFound,
  interactionTitle,
  offerPending,
  offerError,
  offer,
  organizationId,
  orgSlug,
  profileSlug,
  campaignId,
  interactionId,
  secureUrl,
  locationPathname,
  isFetching,
  dateFilter,
  offerCreatedDate,
}: AffiliateLinksPageViewProps): ReactNode {
  if (isLoading) {
    return <AffiliateLinksPageSkeleton />;
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <ProjectPageHeader title="Affiliate Links" />
        <Card className="mt-4">
          <CardContent className="py-8 text-center">
            <p className="text-destructive">
              Failed to load affiliate link details. Please try again.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (interactionNotFound) {
    return (
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <ProjectPageHeader title="Affiliate Links" />
        <Card className="mt-4">
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">
              Affiliate link not found. It may have been removed or you
              don&apos;t have access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-6">
      {/* Header */}
      <ProjectPageHeader
        title={interactionTitle}
        subtitle={
          offer && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Building2 className="h-3 w-3" />
              <span>{offer.clientName}</span>
              <span>&bull;</span>
              <span>{offer.offerName}</span>
            </div>
          )
        }
      />

      {/* Loading state for offer data */}
      {offerPending && <OfferLoadingSkeleton />}

      {/* Error state for offer data */}
      {offerError && (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-destructive">
              Failed to load offer details. Please try again.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Offer content with tabs */}
      {offer && (
        <Tabs defaultValue="overview" className="w-full">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4">
            <div className="space-y-6">
              {/* Affiliate Link or Promo Code Card */}
              {isLinkOffer(offer) && (
                <AffiliateLinkCard
                  offer={offer}
                  organizationId={organizationId}
                  offerUrl={secureUrl}
                />
              )}
              {isPromoOffer(offer) && (
                <PromoCodeCard
                  offer={offer}
                  linkedAffiliateLink={offer.linkedAffiliateLink}
                  organizationId={organizationId}
                  offerUrl={secureUrl}
                />
              )}

              {/* Create Custom Deep Link - shown for link offers with deep linking enabled, but not CreatorStore offers */}
              {isLinkOffer(offer) &&
                offer.creatorDeeplink &&
                !offer.isLandingPageEnabled &&
                secureUrl && (
                  <div className="flex justify-center">
                    <CreateDeepLinkDialog
                      organizationId={organizationId}
                      offerUrl={secureUrl}
                      affiliateOfferLinkId={offer.id}
                      deepLinkDomains={offer.deepLinkDomains}
                      offerLinkUrl={offer.offerLinkUrl}
                      disabled={offer.status.toUpperCase() !== "ACTIVE"}
                    />
                  </div>
                )}

              {/* Share Dialog */}
              <div className="flex justify-center">
                <ShareOfferDialog
                  offer={offer}
                  socialLinks={offer.socialLinks}
                />
              </div>

              {/* Stats - always show clicks for link offers */}
              <OfferStatsDisplay
                stats={offer.stats}
                showClicks={isLinkOffer(offer)}
                offerCreatedDate={offerCreatedDate}
                isLoading={isFetching}
                dateFilter={dateFilter}
              />

              <PayoutTierDetails
                payoutTiers={offer.payoutTiers}
                offerAutomationConfig={offer.offerAutomationConfig}
                stats={offer.stats}
                showPayoutsOnCreatorPortal={offer.showPayoutsOnCreatorPortal}
              />

              {offer.boosts.length > 0 && (
                <BoostRewardsSection
                  boosts={offer.boosts}
                  memberId={offer.memberId}
                  orgSlug={orgSlug}
                  profileSlug={profileSlug}
                  campaignId={campaignId}
                  interactionId={interactionId}
                  interactionRoute={locationPathname}
                />
              )}
            </div>
          </TabsContent>

          <TabsContent value="payments" className="mt-4">
            <PaymentsTab payments={offer.payments} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

// ============================================================================
// Container (hooks + data fetching)
// ============================================================================

function AffiliateLinksPage() {
  const { interactionId, profileSlug } = Route.useParams();
  const context = Route.useRouteContext() as CampaignRouteContext;
  const { programId, profile, organization } = context;
  const location = useLocation();

  // Get interactions for this project to find the secureUrl
  const {
    data: interactionsData,
    isPending: interactionsPending,
    error: interactionsError,
  } = useProjectInteractionsQuery(organization.id, programId, profile.id);

  // Find the specific interaction by ID
  const interaction = interactionsData?.interactions.find(
    (i: CreatorInteraction) => i.id === interactionId,
  );

  const secureUrl = interaction?.secureUrl ?? "";

  // Initial query to get offerCreatedDate
  const { data: initialOfferData } = useAffiliateOfferQuery(
    organization.id,
    secureUrl,
  );

  // Parse offer created date for date filter
  const offerCreatedDate = initialOfferData?.offer?.createdDate
    ? new Date(initialOfferData.offer.createdDate)
    : undefined;

  // Date filter state - hydrated with offerCreatedDate once available
  const dateFilter = useDateFilter({ offerCreatedDate });

  // Convert date range to API parameters
  const dateParams = useMemo(
    () => dateRangeToApiParams(dateFilter.dateRange),
    [dateFilter.dateRange],
  );

  // Fetch affiliate offer data with date filter applied
  const {
    data: offerData,
    isPending: offerPending,
    error: offerError,
    isFetching,
  } = useAffiliateOfferQuery(organization.id, secureUrl, dateParams);

  return (
    <AffiliateLinksPageView
      isLoading={interactionsPending}
      error={interactionsError}
      interactionNotFound={!interactionsPending && !interaction}
      interactionTitle={interaction?.title ?? "Affiliate Links"}
      offerPending={offerPending}
      offerError={offerError}
      offer={offerData?.offer}
      organizationId={organization.id}
      orgSlug={organization.slug}
      profileSlug={profileSlug}
      campaignId={String(programId)}
      interactionId={interactionId}
      secureUrl={secureUrl}
      locationPathname={location.pathname}
      isFetching={isFetching}
      dateFilter={dateFilter}
      offerCreatedDate={offerData?.offer?.createdDate}
    />
  );
}

function AffiliateLinksPageSkeleton() {
  return (
    <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-6">
      <div className="flex items-center gap-4">
        {/* BackButton removed */}

        <div className="flex-1">
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
      </div>
      <OfferLoadingSkeleton />
    </div>
  );
}

function OfferLoadingSkeleton() {
  return (
    <div className="space-y-6">
      {/* Link card skeleton */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col items-center">
            <Skeleton className="h-6 w-32 mb-4" />
            <Skeleton className="h-12 w-full mb-4" />
            <Skeleton className="h-4 w-48" />
          </div>
        </CardContent>
      </Card>

      {/* Share button skeleton */}
      <div className="flex justify-center">
        <Skeleton className="h-10 w-36" />
      </div>

      {/* Stats skeleton */}
      <Card>
        <CardContent className="p-6">
          <Skeleton className="h-5 w-24 mb-4" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="text-center">
                <Skeleton className="h-8 w-16 mx-auto mb-2" />
                <Skeleton className="h-4 w-20 mx-auto" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
